import * as z from 'zod/mini'
import { DateTime } from 'luxon'
import {
  eventFields,
  EventVisibility,
  keyedIssue,
  messageField,
  refineWithKey,
  ReoccurrenceNth,
  ReoccurType,
  ValidationKey,
  WeekDay,
} from '@seiri/shared/common'
import { CreateEventForm, CreateReoccuredEventForm } from '../types/event-form.types'
import { EditEventFormValues } from '../types/event-form.mapper'
import { requiredFormNumber } from './form-schema'

/*
 * The event forms. They hold the pickers' `Date`s and an all-day switch; `toCreateEventBody`,
 * `toCreateEventBlueprintBody` and `toEditEventBody` (types/event-form.mapper.ts) turn them into the
 * bodies. The field rules are the API's (`eventFields`); which times are required follows `allDay`,
 * as it does in the body's union.
 */

const requiredSelect = (key: ValidationKey) => z.string().check(refineWithKey(key, (value: string) => value.length > 0))

const eventFormFields = {
  name: eventFields.name,
  activityId: requiredSelect('validation.activityRequired'),
  placeId: requiredSelect('validation.placeRequired'),
  capacity: requiredFormNumber(eventFields.capacity.check(z.positive())),
  employeeIds: z.array(z.string()),
  visibility: z.enum(EventVisibility),
  allDay: z.boolean(),
  start: z.nullable(z.date()),
  end: z.nullable(z.date()),
  date: z.nullable(z.date()),
  /** Starts at 0 and is required, as a service's price is; 0 is "no price" (the mapper sends none). */
  price: requiredFormNumber(eventFields.price),
  /** A palette hex, or '' for "Podľa činnosti" (the activity's colour). */
  color: z.string(),
  /** HTML from the editor; '' is none. The limit counts the markup, as the API's does. */
  description: eventFields.description,
  /** Minutes before the start a participant may cancel through their link; each sign-up copies it. */
  cancellationDeadline: eventFields.cancellationDeadline,
  /** "Klient môže pridať poznámku": the public sign-up offers a note for the premise. */
  allowCustomerNote: eventFields.allowCustomerNote,
}

type EventTimes = { allDay: boolean; start: Date | null; end: Date | null; date: Date | null }

/** All-day needs a date; a timed event a start and an end after it. Reported on the field to fix. */
const eventTimesRule = <T extends EventTimes>(value: T, ctx: z.core.$RefinementCtx<T>) => {
  if (value.allDay) {
    if (!value.date) ctx.addIssue(keyedIssue('validation.required', ['date']))
    return
  }
  if (!value.start) ctx.addIssue(keyedIssue('validation.required', ['start']))
  if (!value.end) ctx.addIssue(keyedIssue('validation.required', ['end']))
  if (value.start && value.end && value.end.getTime() <= value.start.getTime()) {
    ctx.addIssue(keyedIssue('validation.endAfterStart', ['end']))
  }
}

export const oneTimeEventFormModel = z
  .object({
    ...eventFormFields,
    customerGroupIds: z.array(z.string()),
    allowWaitingList: z.boolean(),
    locked: z.boolean(),
  })
  .check(z.superRefine(eventTimesRule))

export const emptyOneTimeEventForm = (initialDate?: Date): CreateEventForm => {
  const start = initialDate ?? DateTime.now().startOf('hour').plus({ minutes: 30 }).toJSDate()
  return {
    name: '',
    activityId: '',
    placeId: '',
    capacity: 0,
    visibility: EventVisibility.PUBLIC,
    employeeIds: [],
    customerGroupIds: [],
    allDay: false,
    start,
    end: DateTime.fromJSDate(start)
      .plus(initialDate ? { hours: 1 } : { minutes: 30 })
      .toJSDate(),
    date: initialDate ?? DateTime.now().endOf('hour').toJSDate(),
    allowWaitingList: false,
    price: 0,
    color: '',
    description: '',
    locked: false,
    cancellationDeadline: 0,
    allowCustomerNote: false,
  }
}

/**
 * A repeating event: the event fields plus the recurrence. It ends on a date (not before it starts)
 * or after a number of occurrences; weekly and monthly repeats need a weekday, monthly also a week.
 */
export const repeatedEventFormModel = z
  .object({
    ...eventFormFields,
    seriesName: eventFields.seriesName,
    customerGroupIds: z.array(z.string()),
    allowWaitingList: z.boolean(),
    locked: z.boolean(),
    reoccurType: z.enum(ReoccurType),
    weekdays: z.array(z.enum(WeekDay)),
    weeks: z.array(z.enum(ReoccurrenceNth)),
    isReoccurNumOfTimes: z.boolean(),
    reoccurNumOfTimes: z.nullable(z.int().check(z.positive())),
    reoccurFrom: z.date(),
    reoccurUntil: z.nullable(z.date()),
  })
  .check(
    z.superRefine(eventTimesRule),
    z.superRefine((value, ctx) => {
      if (value.isReoccurNumOfTimes && !value.reoccurNumOfTimes) {
        ctx.addIssue(keyedIssue('validation.reoccurCountRequired', ['reoccurNumOfTimes']))
      }
      if (!value.isReoccurNumOfTimes) {
        if (!value.reoccurUntil) {
          ctx.addIssue(keyedIssue('validation.reoccurUntilRequired', ['reoccurUntil']))
        } else if (
          DateTime.fromJSDate(value.reoccurUntil).endOf('day') < DateTime.fromJSDate(value.reoccurFrom).endOf('day')
        ) {
          ctx.addIssue(keyedIssue('validation.reoccurUntilAfterStart', ['reoccurUntil']))
        }
      }
      const needsWeekdays = value.reoccurType === ReoccurType.WEEKLY || value.reoccurType === ReoccurType.MONTHLY
      if (needsWeekdays && !value.weekdays.length) {
        ctx.addIssue(keyedIssue('validation.weekdaysRequired', ['weekdays']))
      }
      if (value.reoccurType === ReoccurType.MONTHLY && !value.weeks.length) {
        ctx.addIssue(keyedIssue('validation.weeksRequired', ['weeks']))
      }
    }),
  )

/** A day picked in the calendar presets the event's times only; the recurrence starts today. */
export const emptyRepeatedEventForm = (initialDate?: Date): CreateReoccuredEventForm => ({
  ...emptyOneTimeEventForm(initialDate),
  seriesName: '',
  reoccurType: ReoccurType.DAILY,
  weekdays: [],
  weeks: [],
  isReoccurNumOfTimes: false,
  reoccurNumOfTimes: 1,
  reoccurFrom: DateTime.now().toJSDate(),
  reoccurUntil: DateTime.now().endOf('day').plus({ days: 7 }).toJSDate(),
})

/**
 * The edit form: the event fields as `EditEventFormValues` holds them, and "Upozorniť účastníkov o zmene e-mailom" with
 * its optional message.
 */
export const editEventFormModel = z
  .object({ ...eventFormFields, notifyCustomers: z.boolean(), message: messageField })
  .check(z.superRefine(eventTimesRule))

export const emptyEditEventForm = (): EditEventFormValues => ({
  name: '',
  activityId: '',
  placeId: '',
  capacity: 0,
  employeeIds: [],
  start: null,
  end: null,
  date: null,
  allDay: false,
  visibility: EventVisibility.PUBLIC,
  price: 0,
  color: '',
  description: '',
  cancellationDeadline: 0,
  allowCustomerNote: false,
  notifyCustomers: false,
  message: '',
})
